import { useTranslation } from "react-i18next";
import { Button, cn } from "@nexus/ui";
import { config } from "@/config";
import { matchesLiveDeploySha, toShortSha } from "@/features/admin/lib/build-identity";
import type { ChangelogEntry } from "@/features/changelog/api/changelog.api";

interface ChangelogServingNowProps {
  entries: ChangelogEntry[];
  /** False while the GitHub list is loading, failed, or switched off. */
  isListReady: boolean;
  onOpenVersion: (versionTag: string) => void;
}

/**
 * The SPA that is serving this session, reconciled against GitHub releases.
 * Lives on Releases — that list is history; this line is "what is running".
 */
export function ChangelogServingNow({
  entries,
  isListReady,
  onOpenVersion,
}: ChangelogServingNowProps) {
  const { t } = useTranslation();

  if (!config.deploySha) return null;

  const matches = isListReady
    ? entries.filter((entry) =>
        matchesLiveDeploySha(config.deploySha, entry.versionSha, entry.shortSha),
      )
    : [];
  const matched = matches.length === 1 ? matches[0] : undefined;
  const isAmbiguous = matches.length > 1;

  const identity = [config.environment, config.deployBranch, toShortSha(config.deploySha)]
    .filter(Boolean)
    .join(" · ");

  return (
    <div
      data-testid="changelog-serving-now"
      className="flex flex-wrap items-baseline gap-x-3 gap-y-1"
    >
      <span className="text-[11px] uppercase tracking-wider text-muted-foreground">
        {t("admin.changelog.servingNow.label", "Serving now")}
      </span>
      <span className="font-mono text-[11px] text-foreground">{identity}</span>
      {matched ? (
        <Button
          variant="ghost"
          size="xs"
          className={cn(
            "h-7 px-1.5 text-xs",
            "min-h-11 min-w-11 [@media(hover:hover)_and_(pointer:fine)]:h-7 [@media(hover:hover)_and_(pointer:fine)]:min-h-0 [@media(hover:hover)_and_(pointer:fine)]:min-w-0",
          )}
          onClick={() => onOpenVersion(matched.versionTag)}
        >
          {t("admin.changelog.servingNow.open", "Open {{version}}", {
            version: matched.versionTag,
          })}
        </Button>
      ) : null}
      {isListReady && isAmbiguous ? (
        <span className="text-xs text-muted-foreground">
          {t(
            "admin.changelog.servingNow.ambiguous",
            "Multiple GitHub rows match this SHA.",
          )}
        </span>
      ) : null}
      {isListReady && !matched && !isAmbiguous ? (
        <span className="text-xs text-muted-foreground">
          {t(
            "admin.changelog.servingNow.notInList",
            "Not in this list yet — GitHub may not have recorded this SHA.",
          )}
        </span>
      ) : null}
    </div>
  );
}
